import { supabase } from './supabase';

export async function requireCurrentAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    throw error;
  }

  const accessToken = data.session?.access_token;
  if (!accessToken) {
    throw new Error('Tu sesion expiro. Recarga la pagina e inicia sesion nuevamente.');
  }

  return accessToken;
}

export async function unwrapFunctionInvokeError(error: unknown): Promise<unknown> {
  if (
    error
    && typeof error === 'object'
    && 'context' in error
    && error.context instanceof Response
  ) {
    try {
      // Algunos Edge Functions devuelven `{ error }` (la mayoria) y otros
      // `{ message }` (errores estructurados, ej. INSUFFICIENT_CRM_MESSAGES
      // de send-campaign -- ver _shared/crmWhatsappBalance.ts) -- se
      // aceptan los dos, sin romper ningun llamador existente.
      const payload = await error.context.clone().json() as { error?: string; message?: string };
      const text = payload?.error || payload?.message;
      if (text) {
        return new Error(text);
      }
    } catch {
      // Ignore JSON parse errors and keep trying.
    }

    try {
      const text = (await error.context.clone().text()).trim();
      if (text) {
        return new Error(text);
      }
    } catch {
      // Ignore text parse errors and keep the original error.
    }
  }

  return error;
}

/**
 * Código de negocio de un error estructurado de Edge Function, si lo trae.
 *
 * `unwrapFunctionInvokeError` colapsa el body a un `Error` con solo el texto,
 * que es lo que necesita el 99% de los llamadores. Cuando la UI tiene que
 * REACCIONAR distinto según el error (no solo mostrarlo), hace falta el `code`:
 * los errores estructurados lo llevan en el body junto al mensaje (ver
 * `NoRecipientsError` y `InsufficientCrmMessagesError` en
 * `supabase/functions/_shared/`).
 *
 * Se lee sobre un clon de la Response, así que no interfiere con
 * `unwrapFunctionInvokeError` si se llaman los dos sobre el mismo error.
 */
export async function readFunctionInvokeErrorCode(error: unknown): Promise<string | null> {
  if (
    error
    && typeof error === 'object'
    && 'context' in error
    && error.context instanceof Response
  ) {
    try {
      // Dos formas conviven en los Edge Functions: `{ code }` en los errores
      // estructurados (INSUFFICIENT_CRM_MESSAGES, NO_RECIPIENTS) y `{ error }`
      // en los más simples (PHONE_LIMIT, PRECHECK_UNAVAILABLE). Se aceptan
      // ambas: sin esto, un 400 con `{ "error": "PHONE_LIMIT" }` quedaba
      // ilegible y el cliente veía "Edge Function returned a non-2xx status
      // code".
      const payload = await error.context.clone().json() as { code?: unknown; error?: unknown };
      if (typeof payload?.code === 'string') return payload.code;
      if (typeof payload?.error === 'string') return payload.error;
      return null;
    } catch {
      return null;
    }
  }
  return null;
}
